/**
 * FeedbackImageUpload Component
 *
 * Image picker/preview/label component for feedback creation and editing.
 * Supports up to 10 images with custom labels, validation, and preview.
 */

import React, { useRef, useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
`;

const ImageCard = styled.div`
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #f9fafb;
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100px;
  object-fit: cover;
  display: block;
`;

const ImageNameInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: none;
  border-top: 1px solid #e5e7eb;
  font-size: 12px;
  background: white;
  box-sizing: border-box;

  &:focus {
    outline: none;
    background: #fffbeb;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    background: #dc2626;
  }
`;

const AddButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  color: #6b7280;
  font-size: 13px;
  gap: 4px;
  transition: all 0.2s;

  &:hover {
    border-color: #f59e0b;
    color: #d97706;
    background: #fffbeb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      border-color: #d1d5db;
      color: #6b7280;
      background: white;
    }
  }

  .icon {
    font-size: 24px;
    line-height: 1;
  }
`;

const CountText = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #ef4444;
`;

const MAX_SIZE_MB = 5;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
const HEIC_TYPES = ['image/heic', 'image/heif'];
const HEIC_BRANDS = ['heic', 'heix', 'mif1', 'msf1', 'heim', 'heis', 'hevc', 'hevx'];

// Detect HEIC/HEIF by reading the ftyp box brand (bytes 4-11).
// Browsers often misreport HEIC as image/jpeg when extension is .jpg, so we
// can't trust file.type alone.
const isHeicByMagic = async (file) => {
  try {
    const head = await file.slice(0, 12).arrayBuffer();
    const bytes = new Uint8Array(head);
    if (String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]) !== 'ftyp') return false;
    const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
    return HEIC_BRANDS.includes(brand);
  } catch {
    return false;
  }
};

const convertHeicToJpeg = async (file) => {
  const { default: heic2any } = await import('heic2any');
  const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  const baseName = file.name.replace(/\.[^/.]+$/, '') || 'image';
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
};

const FeedbackImageUpload = ({
  images = [],
  onChange,
  maxImages = 10,
  disabled = false
}) => {
  const fileInputRef = useRef(null);
  const [converting, setConverting] = useState(false);

  const handleAddClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const remaining = maxImages - images.length;
    const filesToAdd = files.slice(0, remaining);

    const errors = [];
    const newImages = [];
    let needsConversion = false;
    for (const f of filesToAdd) {
      if (HEIC_TYPES.includes(f.type) || await isHeicByMagic(f)) { needsConversion = true; break; }
    }
    if (needsConversion) setConverting(true);

    try {
      for (const original of filesToAdd) {
        let file = original;
        const isHeic = HEIC_TYPES.includes(file.type) || await isHeicByMagic(file);

        if (isHeic) {
          try {
            file = await convertHeicToJpeg(file);
          } catch (err) {
            errors.push(`${original.name}: HEIC conversion failed`);
            continue;
          }
        } else if (!ALLOWED_TYPES.includes(file.type)) {
          errors.push(`${file.name}: invalid type (use JPEG, PNG, GIF, WebP, or HEIC)`);
          continue;
        }

        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
          errors.push(`${original.name}: exceeds ${MAX_SIZE_MB}MB limit`);
          continue;
        }

        newImages.push({
          file,
          name: original.name.replace(/\.[^/.]+$/, ''),
          preview: URL.createObjectURL(file)
        });
      }
    } finally {
      if (needsConversion) setConverting(false);
    }

    if (errors.length > 0) {
      alert('Some images were skipped:\n' + errors.join('\n'));
    }

    if (newImages.length > 0) {
      onChange([...images, ...newImages]);
    }
  };

  const handleRemove = (index) => {
    const updated = [...images];
    // Revoke preview URL to free memory
    if (updated[index].preview && updated[index].preview.startsWith('blob:')) {
      URL.revokeObjectURL(updated[index].preview);
    }
    updated.splice(index, 1);
    onChange(updated);
  };

  const handleNameChange = (index, newName) => {
    const updated = [...images];
    updated[index] = { ...updated[index], name: newName };
    onChange(updated);
  };

  const canAdd = images.length < maxImages && !disabled && !converting;

  return (
    <Container>
      <ImageGrid>
        {images.map((img, index) => (
          <ImageCard key={index}>
            <ImagePreview
              src={img.preview || img.url}
              alt={img.name || `Image ${index + 1}`}
            />
            {!disabled && (
              <RemoveButton onClick={() => handleRemove(index)} title="Remove image">
                &times;
              </RemoveButton>
            )}
            <ImageNameInput
              type="text"
              value={img.name || ''}
              onChange={(e) => handleNameChange(index, e.target.value)}
              placeholder="Image label..."
              disabled={disabled}
              maxLength={100}
            />
          </ImageCard>
        ))}

        {canAdd && (
          <AddButton onClick={handleAddClick} disabled={disabled}>
            <span className="icon">+</span>
            <span>Add Image</span>
          </AddButton>
        )}
        {converting && (
          <AddButton as="div" disabled>
            <span className="icon">⏳</span>
            <span>Converting HEIC...</span>
          </AddButton>
        )}
      </ImageGrid>

      <input
        ref={fileInputRef}
        type="file"
        accept={`${ALLOWED_TYPES.join(',')},.heic,.heif`}
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      <CountText>
        {images.length}/{maxImages} images
        {images.length >= maxImages && <ErrorText style={{ display: 'inline', marginLeft: '8px' }}>Maximum reached</ErrorText>}
      </CountText>
    </Container>
  );
};

export default FeedbackImageUpload;
